<script setup>
import UserSelect from "@/components/ui/UserSelect.vue";
import Avatar from "@/components/ui/Avatar.vue";
import {onMounted, ref, watch, inject, computed} from "vue";
import tasksService from "@/services/tasks.service";
import projectsService from "@/services/projects.service";
import teamService from "@/services/team.service";

const props = defineProps({
    userId: Number,
    projectId: Number,
    initWatchers: {
        type: Array,
        default: () => []
    }
});
const emit = defineEmits(['updateUserId', 'addWatcher', 'removeWatcher']);

const editField = inject("editField");
const errorsUserId = inject("errorsUserId", null);

const assignee = ref(null);
const newAssignee = ref(null);
const assigneeEditMode = ref(false);
const watchers = ref([]);
const usersList = ref([]);
const watchersList = ref([]);
const showAddWatcherModal = ref(false);
const newWatcher = ref(null);

const actualWatchersList = computed(() => {
    return watchersList.value.filter(watcherOption => !watchers.value.find(watcher => watcher?.id === watcherOption.id));
});

watch([() => props.userId, usersList], async () => {
    if (props.userId) {
        if (usersList.value.length) {
            assignee.value = usersList.value.find(user => user.id === props.userId);

            if (!assignee.value) {
                const {data} = await teamService.getUserInfo(props.userId);

                assignee.value = data;
            }
        }
    } else {
        assignee.value = null;
    }
}, {immediate: true});

watch([() => props.initWatchers, usersList], () => {
    watchers.value = usersList.value.filter(user => props.initWatchers.includes(user.id))
}, {immediate: true});

watch(assigneeEditMode, value => {
    if (value) {
        newAssignee.value = {
            ...assignee.value
        };

        editField.value = null;
    } else {
        if (newAssignee.value && newAssignee.value.id !== assignee.value?.id) {
            emit('updateUserId', newAssignee.value.id);
        }
    }
});

watch(editField, value => {
    if (value) {
        assigneeEditMode.value = false
    }
});

onMounted(() => {
    getUsersList();
    getAvailableWatchersList();
});

async function getUsersList() {
    const {data} = await tasksService.getUsersList();
    usersList.value = data;
}

async function getAvailableWatchersList() {
    const {data} = await projectsService.getOptionsForWatchers(props.projectId);

    watchersList.value = data;
}

function newWatcherSelected() {
    if (newWatcher.value) {
        watchers.value.push({...newWatcher.value});
        emit('addWatcher', newWatcher.value.id);
        newWatcher.value = null;
        showAddWatcherModal.value = false;
    }
}

function removeWatcher(index) {
    if (confirm("Remove watcher?")) {
        emit('removeWatcher', watchers.value[index].id);
        watchers.value.splice(index, 1);
    }
}
</script>

<template>
    <div class="access-content__card mb-4 form">
        <header class="access-content__card__header">
            Team
        </header>

        <div class="access-content__card__content">
            <h5 class="heading5 heading5--secondary">Assignee</h5>
            <span v-if="errorsUserId" class="form__error">{{errorsUserId[0]}}</span>

            <UserSelect
                v-if="assigneeEditMode"
                v-click-outside="() => assigneeEditMode = false"
                v-model="newAssignee"
                :options="watchersList"
                class="multiselect-mid mb-3"
                @changed="assigneeEditMode = false"
            />

            <div v-else class="editable mb-4" @click.stop="assigneeEditMode = true">
                <figure v-if="assignee" class="access-content__user mb-4">
                    <Avatar
                        :username="assignee.name"
                        :src="assignee.avatar"
                        :size="39"
                    />

                    <figcaption>
                        <span :class="{deleted: assignee.deleted}">
                            {{assignee.name}}
                        </span>
                    </figcaption>
                </figure>

                <div v-else-if="!userId" class="mb-3">
                    <a class="link mb-2">Select Assignee</a>
                </div>
            </div>

            <h5 class="heading5 heading5--secondary">Watchers</h5>

            <ul class="list-watchers">
                <li v-for="(watcher, index) in watchers" :key="index">
                    <div>
                        <figure class="access-content__user">
                            <Avatar
                                :username="watcher.name"
                                :src="watcher.avatar"
                                :size="39"
                            />

                            <figcaption>
                                {{watcher.name}}
                                <a @click="removeWatcher(index)" class="link">Remove</a>
                            </figcaption>
                        </figure>
                    </div>
                </li>

                <li>
                    <a @click="showAddWatcherModal = true" class="link-add link-add-sm">
                        <span v-if="!watchers.length" class="ml-2">
                            Add Watchers
                        </span>
                    </a>
                </li>
            </ul>
        </div>

        <b-modal v-model="showAddWatcherModal" title="Select Watcher" hide-footer>
            <UserSelect
                v-model="newWatcher"
                :options="actualWatchersList"
                class="multiselect-mid mb-3"
                @changed="newWatcherSelected"
            />
        </b-modal>
    </div>
</template>

<style scoped>

</style>
